import { atom, Atom, type BoxModel } from "tldraw";
import type { GestureKind } from "../../components/highlights/gestureGeometry";
import type { TldrawAgent } from "../TldrawAgent";
import { BaseAgentManager } from "./BaseAgentManager";

export interface ActiveGesture {
  /** Stable identity for React keys and for cancelling this gesture's timer. */
  id: string;
  kind: GestureKind;
  /** Page-space box the mark is drawn around/under. */
  bounds: BoxModel;
  /** tldraw colour name, already defaulted by the caller if the wire omitted it. */
  color?: string;
  /**
   * Seed for the hand-drawn jitter. Derived from the id so the same gesture
   * keeps the same wobble across re-renders (the stroke width is a function of
   * zoom, so it re-renders on every frame of a pinch).
   */
  seed: number;
}

/** How long the stroke takes to draw itself on. Mirrored in canvas.css. */
export const GESTURE_DRAW_MS = 420;
/** How long the finished mark sits there before it starts to go. */
export const GESTURE_HOLD_MS = 700;
/** How long the fade-out takes. Mirrored in canvas.css. */
export const GESTURE_FADE_MS = 320;
/** Total on-screen lifetime of one gesture. */
export const GESTURE_LIFETIME_MS =
  GESTURE_DRAW_MS + GESTURE_HOLD_MS + GESTURE_FADE_MS;

/**
 * Beyond this many at once the canvas reads as scribbled-on rather than pointed
 * at, and the oldest is dropped. A bound also stops a runaway turn from
 * accumulating overlay nodes.
 */
export const MAX_ACTIVE_GESTURES = 4;

/**
 * Transient tutor pointer marks — see `GestureActionUtil`.
 *
 * Modelled on `AgentCursorManager`: an atom the overlay reads through
 * `useValue`, plus self-expiring timers registered on `this.disposables` so a
 * manager torn down mid-animation cannot fire into a dead React tree. Unlike
 * the cursor this holds a LIST — two gestures can legitimately overlap (the
 * gateway does no conflict-keying for them) — so each entry owns its timer.
 *
 * Nothing here touches the tldraw store — that is the whole feature.
 */
export class AgentGestureManager extends BaseAgentManager {
  private $gestures: Atom<ActiveGesture[]>;
  /** id → the `disposables` entry that clears that gesture's expiry timer. */
  private timers = new Map<string, () => void>();
  private nextId = 0;

  constructor(agent: TldrawAgent) {
    super(agent);
    this.$gestures = atom<ActiveGesture[]>("agentGestures", []);
  }

  reset(): void {
    this.clear();
  }

  /**
   * Show a gesture and schedule its own removal.
   *
   * @returns The id of the gesture that was added.
   */
  show(gesture: {
    kind: GestureKind;
    /** Page-space box the mark is drawn around/under. */
    bounds: BoxModel;
    color?: string;
  }): string {
    const id = `gesture-${this.nextId++}`;
    const entry: ActiveGesture = {
      id,
      kind: gesture.kind,
      bounds: gesture.bounds,
      color: gesture.color,
      // A plain counter would give consecutive gestures near-identical jitter
      // (the PRNG is smooth in its seed); the odd multiplier scatters them.
      seed: (this.nextId * 2654435761) % 2147483647,
    };

    const current = this.$gestures.get();
    const next = [...current, entry].slice(-MAX_ACTIVE_GESTURES);
    // Whatever fell off the front is gone from the overlay, so its expiry timer
    // has nothing left to remove — cancel it rather than let it fire into a
    // list it is no longer in.
    for (const dropped of current) {
      if (!next.includes(dropped)) this.clearTimer(dropped.id);
    }
    this.$gestures.set(next);

    const timeout = setTimeout(() => {
      this.clearTimer(id);
      this.expire(id);
    }, GESTURE_LIFETIME_MS);
    // Same disposal idiom as AgentCursorManager.hide() — the pending timeout is
    // captured in a closure on `disposables` so dispose() clears it whether or
    // not it has fired. Unlike the cursor's single timer there is one per
    // gesture, so `clearTimer` retires the closure rather than letting
    // `disposables` grow by one entry per gesture ever shown.
    const disposer = () => clearTimeout(timeout);
    this.timers.set(id, disposer);
    this.disposables.add(disposer);

    return id;
  }

  /**
   * Drop every active gesture immediately, without the fade.
   *
   * Barge-in and error only — NOT clean completion, where the marks are left to
   * finish (see the teardown block in `TldrawAgent`). A gesture outliving the
   * speech it was timed against would point at whatever the next turn draws.
   */
  clear(): void {
    for (const id of [...this.timers.keys()]) {
      this.clearTimer(id);
    }
    if (this.$gestures.get().length > 0) {
      this.$gestures.set([]);
    }
  }

  /** For useValue() consumption in React */
  getGestures(): ActiveGesture[] {
    return this.$gestures.get();
  }

  private expire(id: string): void {
    const current = this.$gestures.get();
    if (!current.some((gesture) => gesture.id === id)) return;
    this.$gestures.set(current.filter((gesture) => gesture.id !== id));
  }

  private clearTimer(id: string): void {
    const disposer = this.timers.get(id);
    if (disposer === undefined) return;
    disposer();
    this.timers.delete(id);
    this.disposables.delete(disposer);
  }
}
